/**
 * Legacy i18n compatibility shim.
 *
 * This file used to hold the entire EN/AR string table in two hand-
 * authored `Strings` objects. As of the F2 design-foundation PR,
 * strings live in `src/i18n/en.json` + `src/i18n/ar.json` and are
 * served by i18next.
 *
 * This shim keeps the legacy `useLocale()` hook signature working
 * (`{ locale, setLocale, t }` where `t` is a flat `Strings` object)
 * so M3-era and M10-M14 components don't need to be rewritten in
 * this PR. New code should use `useTranslation()` from
 * `react-i18next` directly.
 *
 * The R4 retrofit deletes this shim once every consumer has been
 * migrated.
 */

import { useTranslation } from "react-i18next";

import { setLocale as setI18nextLocale } from "../i18n";

export type Locale = "en" | "ar";

/**
 * The legacy flat-strings interface. Component code reads
 * `t.nav_employees`, `t.employees_header` etc. — the keys are
 * preserved by mapping them to the nested i18next paths.
 *
 * @deprecated Use `useTranslation()` from `react-i18next` directly
 * and reference the nested key path (e.g. `t("nav.employees")`).
 */
export interface Strings {
  app_title: string;
  app_subtitle: string;
  nav_employees: string;
  nav_payroll: string;
  nav_onboarding: string;
  nav_integrations: string;
  nav_ai_config: string;
  nav_jobs: string;
  nav_grades: string;
  nav_allowances: string;
  nav_leave_types: string;
  nav_positions: string;
  nav_org_chart: string;
  language_toggle: string;
  loading: string;
  error: string;

  employees_header: string;
  col_code: string;
  col_name: string;
  col_nationality: string;
  col_hire_date: string;
  col_status: string;

  back_to_list: string;
  detail_role: string;
  detail_hire_date: string;
  detail_nationality: string;
  detail_id_number: string;
  detail_contract: string;
  detail_total_wage: string;
  leave_section: string;
  leave_resolution_date: string;
  leave_entitlement: string;
  leave_taken: string;
  leave_remaining: string;
  loan_section: string;
  loan_check: string;
  loan_multiplier: string;
  loan_required_tenure: string;
  loan_total_wage: string;
  loan_existing_balance: string;
  loan_max_eligible: string;
  loan_tenure_eligible: string;
  yes: string;
  no: string;

  payroll_header: string;
  payroll_customer: string;
  payroll_period_start: string;
  payroll_period_end: string;
  payroll_run_button: string;
  payroll_total: string;
  payroll_col_employee: string;
  payroll_col_gross: string;
  payroll_col_gosi: string;
  payroll_col_loan: string;
  payroll_col_net: string;
  payroll_view_trace: string;

  trace_title: string;
  trace_rule: string;
  trace_authority: string;
  trace_resolution_date: string;
  trace_inputs: string;
  trace_outputs: string;
  trace_steps: string;
  trace_nested_calls: string;

  auth_banner: string;
}

/**
 * Build the legacy flat `Strings` object from the current i18next
 * resources. Re-evaluated on every render so the `useTranslation`
 * subscription drives re-renders on language change.
 */
function buildLegacyStrings(
  t: (key: string) => string,
): Strings {
  return {
    app_title: t("app.title"),
    app_subtitle: t("app.subtitle"),
    nav_employees: t("nav.employees"),
    nav_payroll: t("nav.payroll"),
    nav_onboarding: t("nav.onboarding"),
    nav_integrations: t("nav.integrations"),
    nav_ai_config: t("nav.ai_config"),
    nav_jobs: t("nav.jobs"),
    nav_grades: t("nav.grades"),
    nav_allowances: t("nav.allowances"),
    nav_leave_types: t("nav.leave_types"),
    nav_positions: t("nav.positions"),
    nav_org_chart: t("nav.org_chart"),
    language_toggle: t("app.language_toggle"),
    loading: t("common.loading"),
    error: t("common.error"),

    employees_header: t("employee.list_header"),
    col_code: t("employee.col_code"),
    col_name: t("employee.col_name"),
    col_nationality: t("employee.col_nationality"),
    col_hire_date: t("employee.col_hire_date"),
    col_status: t("employee.col_status"),

    back_to_list: t("employee.back_to_list"),
    detail_role: t("employee.detail_role"),
    detail_hire_date: t("employee.detail_hire_date"),
    detail_nationality: t("employee.detail_nationality"),
    detail_id_number: t("employee.detail_id_number"),
    detail_contract: t("employee.detail_contract"),
    detail_total_wage: t("employee.detail_total_wage"),
    leave_section: t("leave.section_balance"),
    leave_resolution_date: t("leave.resolution_date"),
    leave_entitlement: t("leave.entitlement"),
    leave_taken: t("leave.taken"),
    leave_remaining: t("leave.remaining"),
    loan_section: t("loan.section"),
    loan_check: t("loan.check_button"),
    loan_multiplier: t("loan.multiplier"),
    loan_required_tenure: t("loan.required_tenure"),
    loan_total_wage: t("loan.total_wage"),
    loan_existing_balance: t("loan.existing_balance"),
    loan_max_eligible: t("loan.max_eligible"),
    loan_tenure_eligible: t("loan.tenure_eligible"),
    yes: t("common.yes"),
    no: t("common.no"),

    payroll_header: t("payroll.header"),
    payroll_customer: t("payroll.customer"),
    payroll_period_start: t("payroll.period_start"),
    payroll_period_end: t("payroll.period_end"),
    payroll_run_button: t("payroll.run_button"),
    payroll_total: t("payroll.total"),
    payroll_col_employee: t("payroll.col_employee"),
    payroll_col_gross: t("payroll.col_gross"),
    payroll_col_gosi: t("payroll.col_gosi"),
    payroll_col_loan: t("payroll.col_loan"),
    payroll_col_net: t("payroll.col_net"),
    payroll_view_trace: t("payroll.view_trace"),

    trace_title: t("trace.title"),
    trace_rule: t("trace.rule"),
    trace_authority: t("trace.authority"),
    trace_resolution_date: t("trace.resolution_date"),
    trace_inputs: t("trace.inputs"),
    trace_outputs: t("trace.outputs"),
    trace_steps: t("trace.steps"),
    trace_nested_calls: t("trace.nested_calls"),

    auth_banner: t("app.auth_banner"),
  };
}

/**
 * Legacy hook. Returns the locale, a setter that flips i18next + html
 * attributes, and a flat string table. New code should call
 * `useTranslation()` from `react-i18next` directly.
 *
 * @deprecated Use `useTranslation()` from `react-i18next`.
 */
export function useLocale(): {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: Strings;
} {
  const { i18n, t } = useTranslation();
  const locale: Locale = i18n.language === "ar" ? "ar" : "en";
  return {
    locale,
    setLocale: (next: Locale) => {
      void setI18nextLocale(next);
    },
    t: buildLegacyStrings(t),
  };
}

/**
 * Format an employee's display name in the active locale. Lives
 * here because it predates the i18next migration and dozens of
 * pages call it.
 */
export function localizedName(
  emp: {
    first_name_en: string;
    first_name_ar: string;
    last_name_en: string;
    last_name_ar: string;
  },
  locale: Locale,
): string {
  return locale === "ar"
    ? `${emp.first_name_ar} ${emp.last_name_ar}`
    : `${emp.first_name_en} ${emp.last_name_en}`;
}
